// Athlete Mode V2 helpers. Pure functions only (tested in
// src/tests/athleteUtils.test.js). Deal statuses are the shared
// brand_deals statuses read from the athlete's point of view.

export const ATHLETE_DEAL_STATUS = {
  offer_sent: { label: "Pending", tone: "outline", dot: "#edd200", filter: "pending" },
  countered: { label: "In Review", tone: "solid", filter: "pending" },
  accepted: { label: "In Contract", tone: "outline", filter: "active" },
  contract_sent: { label: "In Contract", tone: "outline", filter: "active" },
  signed: { label: "Active", tone: "outline", dot: "#1f9d55", filter: "active" },
  completed: { label: "Completed", tone: "outline", filter: "completed" },
  declined: { label: "Declined", tone: "passive", filter: "declined" },
  cancelled: { label: "Closed", tone: "passive", filter: "declined" },
};

export const HOME_ROW_STATUS = {
  pending: "Pending",
  in_review: "In Review",
  in_contract: "In Contract",
  in_progress: "In Progress",
  completed: "Completed",
  declined: "Declined",
  closed: "Closed",
};

export const dealStatusMeta = (status) =>
  ATHLETE_DEAL_STATUS[status] || { label: status || "Unknown", tone: "passive", filter: "all" };

// The next thing an athlete can do with a deal, keyed by status.
export const athleteDealAction = (deal) => {
  const id = deal?.id;
  switch (deal?.status) {
    case "offer_sent":
      return { label: "Review offer", href: `/athlete/deals/${id}/offer` };
    case "countered":
      return { label: "View", href: `/athlete/deals/${id}/offer` };
    case "accepted":
      return { label: "View", href: `/athlete/deals/${id}/contract` };
    case "contract_sent":
      return deal?.contract?.athleteSignedAt
        ? { label: "View contract", href: `/athlete/deals/${id}/contract` }
        : { label: "Sign contract", href: `/athlete/deals/${id}/contract` };
    case "signed":
      return { label: "View deliverables", href: `/athlete/deals/${id}` };
    case "completed":
      return { label: "View details", href: `/athlete/deals/${id}` };
    default:
      return { label: "View report", href: `/athlete/deals/${id}` };
  }
};

// Placeholder for a value that is not there yet. An en dash, the same one
// StatTile uses; user-facing text never carries an em dash.
export const EMPTY_VALUE = "\u2013";

export const fmtMoney = (n, { cents = false } = {}) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return EMPTY_VALUE;
  const value = Number(n);
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  })}`;
};

export const fmtPct = (n, digits = 0) =>
  n === null || n === undefined || Number.isNaN(Number(n)) ? EMPTY_VALUE : `${Number(n).toFixed(digits)}%`;

export const fmtCompact = (n) => {
  if (n === null || n === undefined) return EMPTY_VALUE;
  const value = Number(n);
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return `${value}`;
};

export const pluralize = (count, noun, plural = `${noun}s`) => `${count} ${count === 1 ? noun : plural}`;

// Age from an ISO date (YYYY-MM-DD). null when unknown.
export const ageFrom = (dateOfBirth, now = new Date()) => {
  if (!dateOfBirth) return null;
  const parts = String(dateOfBirth).slice(0, 10).split("-").map(Number);
  if (parts.length !== 3 || parts.some((p) => Number.isNaN(p))) return null;
  const [y, m, d] = parts;
  let age = now.getFullYear() - y;
  if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)) age -= 1;
  return age;
};

// Counter-form summary chips: what changed vs the original offer.
export const counterSummary = (offer, form) => {
  const compensation = Number(form.compensation) || 0;
  const original = Number(offer?.compensation) || 0;
  const delta = compensation - original;
  const offerUnits = (offer?.deliverables || []).reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
  const formUnits = (form.deliverables || []).reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
  const unitDelta = formUnits - offerUnits;
  const days = Number(form.exclusivityDays) || 0;
  const dayDelta = days - (Number(offer?.exclusivityDays) || 0);
  return {
    compensation: {
      value: compensation,
      chip: delta === 0 ? "Unchanged" : `${delta > 0 ? "+" : "-"}${fmtMoney(Math.abs(delta))}`,
      tone: delta === 0 ? "passive" : delta > 0 ? "green" : "dark",
    },
    exclusivity: {
      value: `${days} Day${days === 1 ? "" : "s"}`,
      chip: dayDelta === 0 ? "Unchanged" : `${dayDelta > 0 ? "+" : "-"}${Math.abs(dayDelta)} days`,
      tone: dayDelta === 0 ? "passive" : "dark",
    },
    deliverables: {
      value: unitDelta === 0 ? "No Changes" : `${formUnits} item${formUnits === 1 ? "" : "s"}`,
      chip: unitDelta === 0 ? "Unchanged" : `${unitDelta > 0 ? "+" : "-"}${Math.abs(unitDelta)} post${Math.abs(unitDelta) === 1 ? "" : "s"}`,
      tone: unitDelta === 0 ? "passive" : "dark",
    },
  };
};

// Cache AI counter analysis from the athlete's side. Uses only the offer,
// the proposed terms and the NILx-derived market range.
export const athleteCounterAnalysis = ({ offer, form, marketRange }) => {
  const compensation = Number(form.compensation) || 0;
  const units = (form.deliverables || []).reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
  const days = Number(form.exclusivityDays) || 0;
  const parts = [];
  if (marketRange && marketRange.min && marketRange.max) {
    if (compensation > marketRange.max) {
      parts.push(
        `Your counter of ${fmtMoney(compensation)} sits above the ${fmtMoney(marketRange.min)}–${fmtMoney(marketRange.max)} range your NILx rates suggest for ${units} deliverable${units === 1 ? "" : "s"}. Expect the brand to push back or counter.`,
      );
    } else if (compensation < marketRange.min) {
      parts.push(
        `Your counter of ${fmtMoney(compensation)} is below the ${fmtMoney(marketRange.min)}–${fmtMoney(marketRange.max)} range your NILx rates suggest. You have room to ask for more.`,
      );
    } else {
      parts.push(
        `Your counter of ${fmtMoney(compensation)} is within the competitive range based on your NILx rates and the ${units} proposed deliverable${units === 1 ? "" : "s"}.`,
      );
    }
  } else if (compensation) {
    parts.push(
      `Your counter of ${fmtMoney(compensation)} covers ${units} deliverable${units === 1 ? "" : "s"}. Link your social accounts to unlock a rate-card comparison.`,
    );
  }
  if (days === 0) parts.push("Removing exclusivity keeps you free to sign with any brand.");
  else if (days <= 14) parts.push(`The ${days}-day exclusivity window is athlete-friendly while still reasonable for the brand.`);
  else parts.push(`A ${days}-day exclusivity window is longer than the 14-day baseline; make sure the pay reflects it.`);
  return parts.join(" ");
};

// Contract review checklist. Guardian item only for minors.
export const contractChecklist = ({ isMinor, guardian }) =>
  [
    { id: "pay", label: "Compensation and payment schedule reviewed" },
    { id: "deliverables", label: "Deliverables and due dates understood" },
    { id: "exclusivity", label: "Exclusivity window acknowledged" },
    isMinor && {
      id: "guardian",
      label: guardian ? `${guardian.name || guardian.email} will co-sign as my guardian` : "A parent or guardian is added to my Circle",
    },
  ].filter(Boolean);

export const filterDeals = (deals, filter, search) => {
  const q = (search || "").trim().toLowerCase();
  return (deals || []).filter((d) => {
    if (filter && filter !== "all" && dealStatusMeta(d.status).filter !== filter) return false;
    if (q) {
      const hay = `${d.campaignName || ""} ${d.brand?.company || ""} ${d.brand?.fullName || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
};

export const circleRoleLabel = (relationship) => {
  switch (relationship) {
    case "Rep":
      return "Representation";
    case "Parent":
    case "Guardian":
      return "Guardian";
    case "Coach":
    case "Program Director":
    case "Trainer":
      return relationship;
    case "Brand":
      return "Brand";
    default:
      return relationship || "Contact";
  }
};

export const circleGroup = (member) => {
  if (member.role === "Brand") return "brands";
  if (member.relationship === "Rep") return "representation";
  return "team";
};

export const circleAccessLabel = (member) => {
  if (member.relationship === "Rep") return member.shareFinancials ? "Deals and financial terms" : "Deal status · no values";
  if (member.relationship === "Parent" || member.relationship === "Guardian") return "Contracts only";
  if (member.permission === "edit") return "Can edit profile";
  if (member.permission === "manage") return "Can manage profile";
  return "Status only · no values";
};

export const circleRelationshipText = (member) => {
  if (member.relationship === "Rep") return `Your rep${member.addedAt ? ` since ${new Date(member.addedAt).getFullYear()}` : ""}`;
  if (member.relationship === "Parent" || member.relationship === "Guardian") return "Signs contracts with you";
  if (member.relationship === "Coach") return "Head coach";
  if (member.relationship === "Program Director") return "Program director";
  if (member.relationship === "Trainer") return "Trainer";
  return member.relationship || "Contact";
};
